import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { m, AnimatePresence } from 'motion/react';
import { Link2 } from 'lucide-react';
import { DistanceUnit, Language } from '../types';
import { SharePayload } from '../utils/sharePayload';
import { formatPace, formatTime, kmToMiles, kmPaceToMilePace } from '../utils/calculator';
import { t } from '../utils/i18n';
import { useDialog } from '../hooks/useDialog';

interface SharePreviewModalProps {
  payload: SharePayload | null;
  language: Language;
  distanceUnit: DistanceUnit;
  onAccept: () => void;
  onDismiss: () => void;
}

export default function SharePreviewModal({ payload, language, distanceUnit, onAccept, onDismiss }: SharePreviewModalProps) {
  const dialogRef = useDialog(!!payload);

  useEffect(() => {
    if (!payload) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
      if (e.key === 'Enter') onAccept();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [payload, onAccept, onDismiss]);

  if (typeof document === 'undefined') return null;

  const unit = payload?.distanceUnit ?? distanceUnit;
  const isMiles = unit === 'mi';
  const rows = payload
    ? [
        [t(language, 'targetDistance'), `${(isMiles ? kmToMiles(payload.distanceKm) : payload.distanceKm).toFixed(2)} ${unit}`],
        [t(language, 'targetPace'), `${formatPace(isMiles ? kmPaceToMilePace(payload.paceSecsPerKm) : payload.paceSecsPerKm)} /${unit}`],
        [t(language, 'targetTime'), formatTime(payload.totalSeconds, true)],
      ]
    : [];

  return createPortal(
    <AnimatePresence>
      {payload && (
        <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center sm:p-4">
          <m.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onDismiss}
            className="fixed inset-0 bg-black/60 backdrop-blur-md"
          />
          <m.div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="share-preview-title"
            tabIndex={-1}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 40 }}
            className="relative w-full max-w-md bg-white/95 dark:bg-[#12121A]/95 backdrop-blur-xl rounded-t-3xl sm:rounded-3xl shadow-2xl z-10 p-6 space-y-5"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-[#7C3AED] text-white flex items-center justify-center">
                <Link2 className="w-5 h-5" />
              </div>
              <div>
                <h2 id="share-preview-title" className="text-lg font-bold text-slate-900 dark:text-white">
                  {t(language, 'sharePreviewTitle')}
                </h2>
                <p className="text-sm text-slate-600 dark:text-slate-400">{t(language, 'sharePreviewDesc')}</p>
              </div>
            </div>

            <dl className="rounded-2xl bg-black/5 dark:bg-slate-800/80 divide-y divide-black/5 dark:divide-white/10">
              {rows.map(([label, value]) => (
                <div key={label} className="flex items-center justify-between px-4 py-3">
                  <dt className="text-sm text-slate-600 dark:text-slate-400">{label}</dt>
                  <dd className="text-base font-bold text-slate-900 dark:text-white">{value}</dd>
                </div>
              ))}
            </dl>

            <div className="flex gap-3">
              <button
                onClick={onDismiss}
                className="flex-1 min-h-12 rounded-2xl bg-black/5 dark:bg-white/10 text-slate-700 dark:text-slate-200 font-semibold whitespace-nowrap active:scale-95 transition-all duration-150 cursor-pointer"
              >
                {t(language, 'shareIgnore')}
              </button>
              <button
                onClick={onAccept}
                className="flex-1 min-h-12 rounded-2xl bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-semibold whitespace-nowrap active:scale-95 transition-all duration-150 cursor-pointer"
              >
                {t(language, 'shareAccept')}
              </button>
            </div>
          </m.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
